/**
 * «Dar de baja» desde la conversación (§5.6): para quien pide por chat que no le escriban más sin usar la
 * palabra BAJA. Queda en no contactar como cualquier baja. Es una tarjeta en la página, no un modal (R2):
 * lo que hace se lee antes de apretar, y el motivo es obligatorio porque queda en la lista de bajas.
 */

import { useId, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { conversacionesApi } from "../../conversacionesApi";
import type { ConversacionDetalle } from "../../types.conversaciones";
import { llamar } from "../base/comun";
import { textoError } from "../../logic/respuesta";

export function TarjetaDarDeBaja({ conversacionId, onCancelar, onListo }: { conversacionId: string; onCancelar: () => void; onListo: () => void }) {
  const idMotivo = useId();
  const [motivo, setMotivo] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [listo, setListo] = useState(false);
  const enCurso = useRef(false);

  const registrar = async () => {
    if (enCurso.current || !motivo.trim()) return;
    enCurso.current = true;
    setEnviando(true);
    setError("");
    const l = await llamar<ConversacionDetalle>(() => conversacionesApi.darDeBaja(conversacionId, motivo.trim()));
    enCurso.current = false;
    setEnviando(false);
    if (!l.ok) { setError(textoError(l)); return; }
    setListo(true);
    onListo();
  };

  return (
    <section className="rounded-2xl border border-red-100 bg-red-50/60 p-4 text-sm text-slate-700" aria-labelledby={`${idMotivo}-titulo`}>
      <h3 id={`${idMotivo}-titulo`} className="font-semibold text-slate-900">Dar de baja</h3>
      {listo ? (
        <>
          <p className="mt-2 text-emerald-800" role="status">Listo: quedó en no contactar.</p>
          <button type="button" onClick={onCancelar} className="mt-3 h-9 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold">Cerrar</button>
        </>
      ) : (
        <>
          <p className="mt-2">
            Usalo cuando la persona pide por chat que no le escriban más, aunque no use la palabra BAJA. Queda en no contactar: no
            le llega ninguna campaña ni plantilla. Si vuelve a escribir, se le puede contestar dentro de las 24 h.
          </p>
          <label htmlFor={idMotivo} className="mt-3 block text-xs font-semibold text-slate-600">Qué pidió</label>
          <textarea id={idMotivo} value={motivo} maxLength={300} rows={2} onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej.: «no me manden más mensajes»"
            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          {error && <p role="alert" className="mt-2 text-red-700">{error}</p>}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" onClick={() => void registrar()} disabled={enviando || !motivo.trim()}
              className="h-9 px-4 rounded-xl bg-red-600 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
              {enviando && <Loader2 size={14} className="animate-spin" />} Registrar la baja
            </button>
            <button type="button" onClick={onCancelar} disabled={enviando} className="h-9 px-4 rounded-xl border border-slate-200 bg-white text-sm font-semibold">Cancelar</button>
          </div>
        </>
      )}
    </section>
  );
}

export default TarjetaDarDeBaja;
